import * as React from "react";
import {
  Box,
  Typography,
  Switch,
  type SxProps,
  type Theme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Controller,
  useFormContext,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

type ToggleFieldBaseProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = {
  name: TName;
  label: React.ReactNode;
  sx?: SxProps<Theme>;
  onToggle?: (checked: boolean) => void;
};

const AtlanteSwitch = styled(Switch)(() => ({
  width: 54,
  height: 30,
  padding: 0,
  display: "flex",

  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(24px)",
      color: "#fff",
    },
  },

  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
    width: 26,
    height: 26,
    borderRadius: 999,
    backgroundColor: "#E5E7EB", // OFF
  },

  "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
    backgroundImage: "linear-gradient(135deg, #6CCB8B 0%, #3975D0 100%)", // ON
  },

  "& .MuiSwitch-track": {
    borderRadius: 999,
    border: "2px solid #C5CDD8",
    backgroundColor: "#FFFFFF",
    opacity: 1,
  },

  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#FFFFFF",
  },
}));

export function ToggleField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({ name, label, sx, onToggle }: ToggleFieldBaseProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const checked = !!field.value;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mt: 2,
              ...sx,
            }}
          >
            <AtlanteSwitch
              checked={checked}
              onChange={(event) => {
                const next = event.target.checked;
                field.onChange(next);
                onToggle?.(next);
              }}
              onBlur={field.onBlur}
            />

            <Typography
              sx={{
                fontSize: "0.95rem",
                color: "#4A5568",
              }}
            >
              {label}
            </Typography>
          </Box>
        );
      }}
    />
  );
}
